import { InputFooter, InputHeader } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Select } from "@amzn/stencil-react-components/select";
import React, { FC } from "react";
import { useController, useFormContext, UseControllerProps } from "react-hook-form";
import { ComboBoxInput } from "@/common/components/ComboBoxInput/combo-box-input";
import { FormField } from "@/common/components/FormField/form-field";
import { COUNTY_COUNTRY_CODES } from "@/common/constants";
import { validateRequiredString } from "@/common/helpers/form-validation.util";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useAddressLocationOptions } from "@/common/hooks/use-address-location-options";
import type {
  AddressFormField,
  AddressFormValues,
  AddressPrefix,
  ChooseDeliveryFormState,
} from "../choose-delivery-form.types";

const STRING_PATH = "replacement.chooseDelivery.address";
/**
 * Digits with the usual separators, at least one digit; the country code is its own field, so no leading "+".
 * HOP's order takes any phone string, so anything stricter is left to address validation.
 */
const PHONE_NUMBER_PATTERN = /^(?=.*\d)[0-9\s\-().]+$/;
/** HOP's maximum length for `shippingAddress.deliveryInstructions`. */
const DELIVERY_INSTRUCTIONS_MAX_LENGTH = 250;
const PHONE_CODE_PREFIX = /^\+\s*/;
const COUNTRY_CODE_WIDTH = 150;

/** Whether a country's addresses have a county, matched on its ISO2 code (today only the UK). */
const usesCounty = (iso2?: string): boolean => COUNTY_COUNTRY_CODES.includes(iso2 ?? "");

type AddressFieldRules = UseControllerProps<ChooseDeliveryFormState, AddressFormField>["rules"];

const REQUIRED: AddressFieldRules = { validate: validateRequiredString };

const PHONE_NUMBER_RULES: AddressFieldRules = {
  validate: {
    required: validateRequiredString,
    pattern: (value: string) =>
      PHONE_NUMBER_PATTERN.test(value) || getString(`${STRING_PATH}.invalidPhoneNumber`),
  },
};

const DELIVERY_INSTRUCTIONS_RULES: AddressFieldRules = {
  validate: (value: string) =>
    value.length <= DELIVERY_INSTRUCTIONS_MAX_LENGTH ||
    getString(`${STRING_PATH}.deliveryInstructionsTooLong`),
};

/** Binds one field of the given address to the form, with a test id like "shippingAddress-zip". */
const useAddressField = (
  prefix: AddressPrefix,
  field: keyof AddressFormValues,
  rules?: AddressFieldRules
) => {
  const { control } = useFormContext<ChooseDeliveryFormState>();
  const name: AddressFormField = `${prefix}.${field}`;
  const { field: controller, fieldState } = useController({ control, name, rules });
  return {
    testId: `${prefix}-${field}`,
    value: controller.value,
    onChange: controller.onChange,
    onBlur: controller.onBlur,
    inputRef: controller.ref,
    error: fieldState.error?.message,
  };
};

export interface AddressFormFieldsProps {
  prefix: AddressPrefix;
}

/**
 * HOP's address fields in checkout's order, then the phone and the optional delivery instructions. The required
 * fields are provisional until HOP confirms its order contract (spec OQ-9). Picking a country also sets its phone
 * code and clears the county for countries without one.
 */
export const AddressFormFields: FC<AddressFormFieldsProps> = ({ prefix }) => {
  const { setValue, resetField } = useFormContext<ChooseDeliveryFormState>();
  const country = useAddressField(prefix, "country", REQUIRED);
  const state = useAddressField(prefix, "state", REQUIRED);
  const {
    countries,
    phoneCodes,
    matchedCountry,
    states,
    cities,
    isStatesLoading,
    isCitiesLoading,
  } = useAddressLocationOptions(country.value, state.value);
  const isCountyEnabled = usesCounty(matchedCountry?.iso2);

  const line1 = useAddressField(prefix, "line1", REQUIRED);
  const line2 = useAddressField(prefix, "line2");
  const line3 = useAddressField(prefix, "line3");
  const city = useAddressField(prefix, "city", REQUIRED);
  const zip = useAddressField(prefix, "zip", REQUIRED);
  const county = useAddressField(prefix, "county", {
    validate: (value: string) => !isCountyEnabled || validateRequiredString(value),
  });
  const countryCode = useAddressField(prefix, "countryCode", REQUIRED);
  const phoneNumber = useAddressField(prefix, "phoneNumber", PHONE_NUMBER_RULES);
  const deliveryInstructions = useAddressField(
    prefix,
    "deliveryInstructions",
    DELIVERY_INSTRUCTIONS_RULES
  );

  const handleCountryChange = (countryName: string) => {
    const selected = countries.find((candidate) => candidate.name === countryName);
    country.onChange(countryName);
    setValue(`${prefix}.countryCode`, selected?.phonecode ?? "", { shouldDirty: true });
    if (!usesCounty(selected?.iso2)) {
      resetField(`${prefix}.county`, { defaultValue: "" });
    }
  };

  return (
    <Col gridGap="S200" width="100%">
      <Row gridGap="S300" width="100%">
        <Col gridGap="S200" width="50%">
          <InputHeader
            labelText={getString(`${STRING_PATH}.country`)}
            htmlFor={country.testId}
            required
            error={Boolean(country.error)}
          />
          <Select
            id={country.testId}
            dataTestId={country.testId}
            isSearchable
            isMulti={false}
            width="100%"
            options={countries.map((candidate) => candidate.name)}
            value={country.value || undefined}
            error={Boolean(country.error)}
            onChange={(value?: string) => handleCountryChange(value ?? "")}
          />
          {country.error && (
            <InputFooter
              id={`${country.testId}-error`}
              error
              data-testid={`${country.testId}-error`}
            >
              {country.error}
            </InputFooter>
          )}
        </Col>
        <Col width="50%">
          <ComboBoxInput
            label={getString(`${STRING_PATH}.state`)}
            testId={state.testId}
            value={state.value}
            options={states}
            isLoading={isStatesLoading}
            required
            error={state.error}
            onChange={state.onChange}
          />
        </Col>
      </Row>
      <Row gridGap="S300" width="100%">
        <Col width={isCountyEnabled ? "50%" : "100%"}>
          <ComboBoxInput
            label={getString(`${STRING_PATH}.city`)}
            testId={city.testId}
            value={city.value}
            options={cities}
            isLoading={isCitiesLoading}
            required
            error={city.error}
            onChange={city.onChange}
          />
        </Col>
        {isCountyEnabled && (
          <Col width="50%">
            <FormField {...county} label={getString(`${STRING_PATH}.county`)} required />
          </Col>
        )}
      </Row>
      <Col width="50%">
        <FormField {...zip} label={getString(`${STRING_PATH}.zip`)} required />
      </Col>
      <FormField
        {...line1}
        label={getString(`${STRING_PATH}.line1`)}
        description={getString(`${STRING_PATH}.line1Description`)}
        required
      />
      <FormField
        {...line2}
        label={getString(`${STRING_PATH}.line2`)}
        description={getString(`${STRING_PATH}.line2Description`)}
      />
      <FormField
        {...line3}
        label={getString(`${STRING_PATH}.line3`)}
        description={getString(`${STRING_PATH}.line3Description`)}
      />
      <Row gridGap="S300" width="100%">
        <Col gridGap="S200" width={COUNTRY_CODE_WIDTH} flexShrink={0}>
          <InputHeader
            labelText={getString(`${STRING_PATH}.countryCode`)}
            htmlFor={countryCode.testId}
            required
            error={Boolean(countryCode.error)}
          />
          <Select
            id={countryCode.testId}
            dataTestId={countryCode.testId}
            isSearchable
            isMulti={false}
            width="100%"
            options={phoneCodes}
            placeholder={getString(`${STRING_PATH}.countryCodePlaceholder`)}
            noOptionsText={getString(`${STRING_PATH}.countryCodeLoading`)}
            value={countryCode.value ? `+ ${countryCode.value}` : undefined}
            error={Boolean(countryCode.error)}
            onChange={(value?: string) =>
              countryCode.onChange(value?.replace(PHONE_CODE_PREFIX, "") ?? "")
            }
          />
          {countryCode.error && (
            <InputFooter
              id={`${countryCode.testId}-error`}
              error
              data-testid={`${countryCode.testId}-error`}
            >
              {countryCode.error}
            </InputFooter>
          )}
        </Col>
        <Col flex={1}>
          <FormField
            {...phoneNumber}
            label={getString(`${STRING_PATH}.phoneNumber`)}
            description={getString(`${STRING_PATH}.phoneNumberDescription`)}
            required
          />
        </Col>
      </Row>
      <FormField
        {...deliveryInstructions}
        label={getString(`${STRING_PATH}.deliveryInstructions`)}
        placeholder={getString(`${STRING_PATH}.deliveryInstructionsPlaceholder`)}
        isTextArea
      />
    </Col>
  );
};
